import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { UnifiedMessage as WebSocketMessage } from "@/types/chat/message.types";
import { logger } from "@/utils/logger";

interface WebSocketState {
  isConnected: boolean;
  isConnecting: boolean;
  isReconnecting: boolean;
  error: string | null;
  reconnectAttempts: number;
  lastPingTime: number | null;
  messages: WebSocketMessage[];
  activeSessions: string[];
  messageQueue: {
    [sessionId: string]: WebSocketMessage[];
  };
  connectionHealth: {
    lastCheck: number;
    status: "healthy" | "degraded" | "unhealthy";
  };
}

const initialState: WebSocketState = {
  isConnected: false,
  isConnecting: false,
  isReconnecting: false,
  error: null,
  reconnectAttempts: 0,
  lastPingTime: null,
  messages: [],
  activeSessions: [],
  messageQueue: {},
  connectionHealth: {
    lastCheck: Date.now(),
    status: "unhealthy",
  },
};

const websocketSlice = createSlice({
  name: "websocket",
  initialState,
  reducers: {
    setConnected: (state, action: PayloadAction<boolean | undefined>) => {
      const newStatus = action.payload !== undefined ? action.payload : true;
      logger.info("[websocketSlice] 🔄 setConnected chamado:", {
        payload: action.payload,
        newStatus,
        previousState: state.isConnected,
        timestamp: new Date().toISOString()
      });
      state.isConnected = newStatus;
      state.isReconnecting = false;
      if (newStatus) {
        state.error = null;
      }
      logger.info("[websocketSlice] ✅ Estado atualizado:", {
        isConnected: state.isConnected,
        isReconnecting: state.isReconnecting,
        error: state.error
      });
    },
    setDisconnected: (state) => {
      logger.info("[websocketSlice] 🔄 setDisconnected chamado:", {
        previousState: {
          isConnected: state.isConnected,
          isConnecting: state.isConnecting,
          isReconnecting: state.isReconnecting
        },
        timestamp: new Date().toISOString()
      });
      state.isConnected = false;
      state.isConnecting = false;
      state.isReconnecting = false;
      logger.info("[websocketSlice] ✅ Estado atualizado após setDisconnected:", {
        isConnected: state.isConnected,
        isConnecting: state.isConnecting,
        isReconnecting: state.isReconnecting
      });
    },
    setConnecting: (state) => {
      logger.info("[websocketSlice] 🔄 setConnecting chamado:", {
        previousState: {
          isConnecting: state.isConnecting,
          error: state.error
        },
        timestamp: new Date().toISOString()
      });
      state.isConnecting = true;
      state.error = null;
      logger.info("[websocketSlice] ✅ Estado atualizado após setConnecting:", {
        isConnecting: state.isConnecting,
        error: state.error
      });
    },
    setReconnecting: (state, action: PayloadAction<boolean>) => {
      logger.info("[websocketSlice] 🔄 setReconnecting chamado:", {
        payload: action.payload,
        previousState: {
          isConnected: state.isConnected,
          isReconnecting: state.isReconnecting
        },
        timestamp: new Date().toISOString()
      });
      state.isReconnecting = action.payload;
      logger.info("[websocketSlice] ✅ Estado atualizado após setReconnecting:", {
        isConnected: state.isConnected,
        isReconnecting: state.isReconnecting
      });
    },
    setError: (state, action: PayloadAction<string | null>) => {
      logger.info("[websocketSlice] 🔄 setError chamado:", {
        payload: action.payload,
        previousState: {
          isConnected: state.isConnected,
          isReconnecting: state.isReconnecting,
          error: state.error
        },
        timestamp: new Date().toISOString()
      });
      state.error = action.payload;
      if (action.payload) {
        state.isConnected = false;
        state.isReconnecting = false;
      }
      logger.info("[websocketSlice] ✅ Estado atualizado após setError:", {
        isConnected: state.isConnected,
        isReconnecting: state.isReconnecting,
        error: state.error
      });
    },
    resetReconnectAttempts: (state) => {
      logger.info("[websocketSlice] 🔄 resetReconnectAttempts chamado:", {
        previousState: {
          reconnectAttempts: state.reconnectAttempts,
          isReconnecting: state.isReconnecting
        },
        timestamp: new Date().toISOString()
      });
      state.reconnectAttempts = 0;
      state.isReconnecting = false;
      logger.info("[websocketSlice] ✅ Estado atualizado após resetReconnectAttempts:", {
        reconnectAttempts: state.reconnectAttempts,
        isReconnecting: state.isReconnecting
      });
    },
    setReconnectAttempts: (state, action: PayloadAction<number>) => {
      logger.info("[websocketSlice] 🔄 setReconnectAttempts chamado:", {
        payload: action.payload,
        previousState: {
          reconnectAttempts: state.reconnectAttempts
        },
        timestamp: new Date().toISOString()
      });
      state.reconnectAttempts = action.payload;
      logger.info("[websocketSlice] ✅ Estado atualizado após setReconnectAttempts:", {
        reconnectAttempts: state.reconnectAttempts
      });
    },
    setLastPingTime: (state, action: PayloadAction<number>) => {
      state.lastPingTime = action.payload;
    },
    addMessage: (state, action: PayloadAction<WebSocketMessage>) => {
      logger.info("[websocketSlice] 🔄 addMessage chamado:", {
        messageId: action.payload.id,
        role: action.payload.role,
        contentLength: action.payload.content?.length || 0,
        contentPreview: action.payload.content?.substring(0, 100) + "...",
        timestamp: new Date().toISOString(),
        previousMessagesCount: state.messages.length
      });
      
      state.messages.push(action.payload);
      
      logger.info("[websocketSlice] ✅ Mensagem adicionada com sucesso:", {
        messageId: action.payload.id,
        newMessagesCount: state.messages.length,
        timestamp: new Date().toISOString()
      });
    },
    setMessage: (state, action: PayloadAction<WebSocketMessage>) => {
      const index = state.messages.findIndex(msg => msg.id === action.payload.id);
      if (index !== -1) {
        state.messages[index] = action.payload;
      } else {
        state.messages.push(action.payload);
      }
    },
    updateMessageStatus: (state, action: PayloadAction<{ id: string; status: WebSocketMessage["status"] }>) => {
      const message = state.messages.find(msg => msg.id === action.payload.id);
      if (message) {
        message.status = action.payload.status;
      }
    },
    addActiveSession: (state, action: PayloadAction<string>) => {
      if (!state.activeSessions.includes(action.payload)) {
        state.activeSessions.push(action.payload);
      }
    },
    removeActiveSession: (state, action: PayloadAction<string>) => {
      state.activeSessions = state.activeSessions.filter(session => session !== action.payload);
    },
    addMessageToQueue: (state, action: PayloadAction<{ sessionId: string; message: WebSocketMessage }>) => {
      const { sessionId, message } = action.payload;
      if (!state.messageQueue[sessionId]) {
        state.messageQueue[sessionId] = [];
      }
      state.messageQueue[sessionId].push(message);
    },
    removeMessageFromQueue: (state, action: PayloadAction<{ sessionId: string; messageId: string }>) => {
      const { sessionId, messageId } = action.payload;
      if (state.messageQueue[sessionId]) {
        state.messageQueue[sessionId] = state.messageQueue[sessionId].filter(
          msg => msg.id !== messageId
        );
      }
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    resetState: (state) => {
      state.messages = [];
      state.error = null;
      state.isConnected = false;
      state.isConnecting = false;
      state.isReconnecting = false;
      state.reconnectAttempts = 0;
      state.activeSessions = [];
      state.messageQueue = {};
    },
    clearError: (state) => {
      state.error = null;
      state.connectionHealth.status = "healthy";
    },
    updateConnectionHealth: (state, action: PayloadAction<WebSocketState["connectionHealth"]>) => {
      state.connectionHealth = action.payload;
    },
  },
});

export const {
  setConnected,
  setDisconnected,
  setConnecting,
  setReconnecting,
  setError,
  resetReconnectAttempts,
  setReconnectAttempts,
  setLastPingTime,
  addMessage,
  setMessage,
  updateMessageStatus,
  addActiveSession,
  removeActiveSession,
  addMessageToQueue,
  removeMessageFromQueue,
  clearMessages,
  resetState,
  clearError,
  updateConnectionHealth,
} = websocketSlice.actions;

// Selectors
export const selectWebSocketMessages = (state: { websocket: WebSocketState }) =>
  state.websocket.messages;
export const selectWebSocketConnected = (state: {
  websocket: WebSocketState;
}) => state.websocket.isConnected;
export const selectWebSocketError = (state: { websocket: WebSocketState }) =>
  state.websocket.error;
export const selectWebSocketConnectionHealth = (state: {
  websocket: WebSocketState;
}) => state.websocket.connectionHealth;

export default websocketSlice.reducer;

export type { WebSocketState };
